import Phaser from 'phaser';
import { GAME_CONFIG, PLAYER_CONFIG } from '../../config/game';
import { Player } from '../../entities/Player';
import { MobileControls } from '../../ui/MobileControls';
import { GameStateManager } from '../../managers/GameStateManager';

// Edge physics thresholds - same as boss scenes
const EDGE_THRESHOLDS = {
  STAND: 180,      // >180: can stand fully
  SLOW_FALL: 100,  // 100-180: slow fall (friction)
  BOOST: 50,       // 50-100: jump boost only
};

interface EdgeData {
  width: number;
  height: number;
  scale: number;
  edges: number[][];
}

export interface RunSceneConfig {
  levelId: string;
  levelName: string;
  backgroundKey: string;
  targetDistance: number; // Distance to complete level
  scrollSpeed: number; // Base scroll speed
}

/**
 * BaseRunScene - Template for endless runner / obstacle course levels
 * 
 * Provides:
 * - Auto-scrolling background
 * - Player with jump/dash controls
 * - Obstacle spawning system
 * - Collectible system
 * - Distance tracking
 * - Win condition based on distance
 */
export abstract class BaseRunScene extends Phaser.Scene {
  protected player!: Player;
  protected ground!: Phaser.Physics.Arcade.StaticGroup;
  protected obstacles!: Phaser.Physics.Arcade.Group;
  protected collectibles!: Phaser.Physics.Arcade.Group;
  protected hearts: Phaser.GameObjects.Image[] = [];
  protected isPaused: boolean = false;
  protected isGameOver: boolean = false;
  protected cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  protected jumpKey!: Phaser.Input.Keyboard.Key;
  protected dashKey!: Phaser.Input.Keyboard.Key;
  protected mobileControls?: MobileControls;
  
  protected config: RunSceneConfig;
  protected distance: number = 0;
  protected distanceText!: Phaser.GameObjects.Text;
  protected collected: number = 0;
  protected scrollSpeed: number = 0;
  protected bgLayers: Phaser.GameObjects.TileSprite[] = [];
  
  // Edge physics for Sobel-based jumping
  protected edgeData?: EdgeData;
  protected edgeOverlay?: Phaser.GameObjects.TileSprite;
  protected currentJumpBoost: number = 1.0;

  constructor(sceneKey: string, config: RunSceneConfig) {
    super(sceneKey);
    this.config = config;
  }

  create() {
    const { width, height } = GAME_CONFIG;
    this.isGameOver = false;
    this.isPaused = false;
    this.distance = 0;
    this.collected = 0;
    this.scrollSpeed = this.config.scrollSpeed;
    this.currentJumpBoost = 1.0;

    // Create scrolling background
    this.createBackground(width, height);

    // Create ground
    this.createGround(width, height);
    
    // Load edge physics data for Sobel-based jumping
    this.loadEdgeData();
    this.createEdgeOverlay(width, height);

    // Create groups
    this.obstacles = this.physics.add.group();
    this.collectibles = this.physics.add.group();

    // Create player (positioned on left side, running right)
    this.player = new Player(this, width * 0.2, height - 150);
    this.player.setDepth(100); // Ensure player renders in front

    // Collisions
    this.physics.add.collider(this.player, this.ground);
    
    this.physics.add.overlap(
      this.player,
      this.obstacles,
      this.handleObstacleHit as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this
    );

    this.physics.add.overlap(
      this.player,
      this.collectibles,
      this.handleCollectiblePickup as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this
    );

    // Setup controls
    this.setupControls();

    // UI
    this.createUI(width);

    // Show level intro
    this.showLevelIntro(width, height);

    // Fade in
    this.cameras.main.fadeIn(500, 0, 0, 0);

    // Start spawning obstacles after intro
    this.time.delayedCall(2000, () => {
      this.startSpawning();
    });
  }

  protected createBackground(width: number, height: number) {
    // Try to use generated background as tile sprite
    if (this.textures.exists(this.config.backgroundKey)) {
      const bg = this.add.tileSprite(width / 2, height / 2, width, height, this.config.backgroundKey);
      bg.setDepth(0);
      this.bgLayers.push(bg);
    } else {
      // Fallback gradient
      const bg = this.add.graphics();
      bg.fillGradientStyle(0x87ceeb, 0x87ceeb, 0x4a90d9, 0x4a90d9, 1);
      bg.fillRect(0, 0, width, height);
    }
  }

  protected createGround(width: number, height: number) {
    this.ground = this.physics.add.staticGroup();

    // Wide ground platform
    const groundRect = this.add.rectangle(width / 2, height - 40, width * 3, 80, 0x5d4037);
    this.physics.add.existing(groundRect, true);
    this.ground.add(groundRect);

    // Visual floor line
    const floorLine = this.add.graphics();
    floorLine.fillStyle(0x4a3429, 1);
    floorLine.fillRect(0, height - 80, width, 80);
    floorLine.setDepth(1);
  }

  // Load Sobel edge data from JSON
  protected loadEdgeData() {
    const edgeKey = `edges_${this.config.levelId}`;
    try {
      this.edgeData = this.cache.json.get(edgeKey) as EdgeData;
      if (this.edgeData) {
        console.log(`[${this.config.levelName}] Loaded edge data: ${this.edgeData.width}x${this.edgeData.height}`);
      }
    } catch (e) {
      // No edge data for this level - that's ok
    }
  }

  // Create gold-tinted edge overlay for visual feedback (as TileSprite so it scrolls)
  protected createEdgeOverlay(width: number, height: number) {
    const frictionKey = `friction_${this.config.levelId}`;
    if (this.textures.exists(frictionKey)) {
      // Use TileSprite so it can scroll with the background
      this.edgeOverlay = this.add.tileSprite(width / 2, height / 2, width, height, frictionKey);
      this.edgeOverlay.setTint(0xffd700); // Gold tint
      this.edgeOverlay.setBlendMode(Phaser.BlendModes.ADD);
      this.edgeOverlay.setAlpha(0.3);
      this.edgeOverlay.setDepth(2);
      
      // Subtle shimmer animation
      this.tweens.add({
        targets: this.edgeOverlay,
        alpha: { from: 0.25, to: 0.35 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  // Get edge strength at a specific pixel position
  protected getEdgeStrength(x: number, y: number): number {
    if (!this.edgeData) return 0;
    
    const scale = this.edgeData.scale || 2;
    const ix = Math.floor(x / scale);
    const iy = Math.floor(y / scale);
    
    if (iy < 0 || iy >= this.edgeData.edges.length) return 0;
    if (ix < 0 || ix >= (this.edgeData.edges[iy]?.length || 0)) return 0;
    
    return this.edgeData.edges[iy][ix] || 0;
  }

  // Apply edge-based physics to player
  protected applyEdgePhysics() {
    if (!this.player) return;
    
    const playerBody = this.player.body as Phaser.Physics.Arcade.Body;
    
    // If player is on real ground, don't mess with edge physics
    const onRealGround = playerBody.blocked.down || playerBody.touching.down;
    if (onRealGround) {
      this.player.setExternalGround(false); // Let real ground handle it
      this.currentJumpBoost = 1.0;
      return;
    }
    
    // No edge data? Use standard physics
    if (!this.edgeData) {
      this.player.setExternalGround(false);
      return;
    }
    
    const playerHeight = playerBody.height;
    
    // Sample edge strength at player's feet
    const feetX = this.player.x;
    const feetY = this.player.y + playerHeight / 2;
    const edgeStrength = this.getEdgeStrength(feetX, feetY);
    
    // Sample left and right for wider detection
    const leftStrength = this.getEdgeStrength(feetX - 10, feetY);
    const rightStrength = this.getEdgeStrength(feetX + 10, feetY);
    const maxStrength = Math.max(edgeStrength, leftStrength, rightStrength);
    
    // Apply physics based on edge strength
    if (maxStrength > EDGE_THRESHOLDS.STAND) {
      // Strong edge: full support (only when falling or standing still)
      if (playerBody.velocity.y > 0) {
        playerBody.setVelocityY(0);
        this.player.setExternalGround(true);
      } else if (playerBody.velocity.y === 0) {
        // Already standing still on edge
        this.player.setExternalGround(true);
      }
      this.currentJumpBoost = 1.0 + (maxStrength - EDGE_THRESHOLDS.STAND) / 200;
    } else if (maxStrength > EDGE_THRESHOLDS.SLOW_FALL) {
      // Medium edge: friction/slow fall
      if (playerBody.velocity.y > 0) {
        playerBody.setVelocityY(playerBody.velocity.y * 0.85);
      }
      this.currentJumpBoost = 1.2;
      this.player.setExternalGround(false);
    } else if (maxStrength > EDGE_THRESHOLDS.BOOST) {
      // Weak edge: jump boost only
      this.currentJumpBoost = 1.15;
      this.player.setExternalGround(false);
    } else {
      // No edge support
      this.currentJumpBoost = 1.0;
      this.player.setExternalGround(false);
    }
  }

  protected setupControls() {
    if (this.input.keyboard) {
      this.cursors = this.input.keyboard.createCursorKeys();
      this.jumpKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
      this.dashKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);

      this.input.keyboard.on('keydown-ESC', () => this.togglePause());
      this.input.keyboard.on('keydown-P', () => this.togglePause());
    }

    // Mobile controls
    if (MobileControls.shouldShow()) {
      this.mobileControls = new MobileControls(this);
      
      this.events.on('mobile-jump-down', () => {
        if (!this.isPaused && !this.isGameOver) {
          // Use jumpWithBoost for edge physics support
          if (this.player.isOnGround()) {
            this.player.jumpWithBoost(this.currentJumpBoost);
            this.currentJumpBoost = 1.0; // Reset after use
          }
        }
      });
      
      this.events.on('mobile-dash-down', () => {
        if (!this.isPaused && !this.isGameOver) {
          this.player.dash(1); // Always dash right in runner
        }
      });
    }
  }

  protected createUI(width: number) {
    // Hearts
    for (let i = 0; i < PLAYER_CONFIG.maxHealth; i++) {
      const heart = this.add.image(30 + i * 30, 30, 'heart');
      heart.setDepth(100);
      this.hearts.push(heart);
    }

    // Distance meter
    this.distanceText = this.add.text(width - 20, 30, '0m', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(1, 0.5).setDepth(100);

    // Progress bar
    const progressBg = this.add.graphics();
    progressBg.fillStyle(0x333333, 0.8);
    progressBg.fillRect(width / 2 - 100, 55, 200, 12);
    progressBg.setDepth(100);

    // Level name
    this.add.text(width / 2, 30, this.config.levelName, {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffd700',
    }).setOrigin(0.5).setDepth(100);
  }

  protected updateProgressBar() {
    const { width } = GAME_CONFIG;
    const progress = Math.min(1, this.distance / this.config.targetDistance);
    
    // Update progress bar
    const progressBar = this.add.graphics();
    progressBar.fillStyle(0x44ff44, 1);
    progressBar.fillRect(width / 2 - 98, 57, 196 * progress, 8);
    progressBar.setDepth(100);
  }

  protected showLevelIntro(width: number, height: number) {
    const levelText = this.add.text(width / 2, height / 2 - 20, this.config.levelName, {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(200);

    const goalText = this.add.text(width / 2, height / 2 + 20, `Reach ${this.config.targetDistance}m!`, {
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5).setDepth(200);

    this.tweens.add({
      targets: [levelText, goalText],
      alpha: 0,
      delay: 1500,
      duration: 500,
      onComplete: () => {
        levelText.destroy();
        goalText.destroy();
        
        // Show tutorial if first time
        this.showTutorialIfNeeded();
      },
    });
  }
  
  /**
   * Show tutorial overlay if player hasn't seen it for runner levels
   */
  protected showTutorialIfNeeded() {
    const tutorialKey = 'tutorial_runner_seen';
    if (typeof localStorage !== 'undefined' && localStorage.getItem(tutorialKey)) {
      return;
    }
    
    const { width, height } = GAME_CONFIG;
    
    // Create tutorial overlay
    const overlay = this.add.container(width / 2, height / 2);
    overlay.setDepth(250);
    
    const bg = this.add.rectangle(0, 0, 300, 180, 0x000000, 0.85);
    
    const title = this.add.text(0, -65, '🎮 CONTROLS', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#ffd700',
    }).setOrigin(0.5);
    
    const controls = this.add.text(0, 0, 
      '↑ or Jump Button: Jump\n' +
      'SHIFT or Dash Button: Dash\n\n' +
      '💡 Jump over obstacles!\n' +
      '💡 Dash makes you invincible!',
    {
      fontFamily: 'Georgia, serif',
      fontSize: '12px',
      color: '#ffffff',
      align: 'center',
      lineSpacing: 4,
    }).setOrigin(0.5);
    
    const tapText = this.add.text(0, 65, 'Tap anywhere to start', {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#888888',
    }).setOrigin(0.5);
    
    overlay.add([bg, title, controls, tapText]);
    
    // Pause game while tutorial is shown
    this.isPaused = true;
    this.physics.pause();
    
    // Tap to dismiss
    bg.setInteractive();
    bg.on('pointerdown', () => {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(tutorialKey, 'true');
      }
      overlay.destroy();
      this.isPaused = false;
      this.physics.resume();
    });
    
    // Also dismiss with any key
    if (this.input.keyboard) {
      const dismissHandler = () => {
        if (overlay && overlay.scene) {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(tutorialKey, 'true');
          }
          overlay.destroy();
          this.isPaused = false;
          this.physics.resume();
          this.input.keyboard?.off('keydown', dismissHandler);
        }
      };
      this.input.keyboard.on('keydown', dismissHandler);
    }
  }

  protected handleObstacleHit(
    player: Phaser.GameObjects.GameObject,
    obstacle: Phaser.GameObjects.GameObject
  ) {
    if (this.isGameOver) return;

    const playerObj = player as Player;
    const obs = obstacle as Phaser.Physics.Arcade.Sprite;

    // Check if dashing (invincible)
    if (playerObj.isDashing && playerObj.isDashing()) {
      obs.destroy();
      return;
    }

    // Take damage
    obs.destroy();
    const alive = playerObj.takeDamage();
    this.updateHearts();

    if (!alive) {
      this.onDefeat();
    }
  }

  protected handleCollectiblePickup(
    _player: Phaser.GameObjects.GameObject,
    collectible: Phaser.GameObjects.GameObject
  ) {
    const col = collectible as Phaser.Physics.Arcade.Sprite;
    col.destroy();
    this.collected++;
    this.onCollect();
  }

  protected updateHearts() {
    const currentHealth = this.player.getHealth();
    this.hearts.forEach((heart, i) => {
      const wasFull = heart.texture.key === 'heart';
      const nowFull = i < currentHealth;
      
      heart.setTexture(nowFull ? 'heart' : 'heart_empty');
      
      // Shake hearts when losing health
      if (wasFull && !nowFull) {
        this.shakeHearts();
      }
    });
  }
  
  protected shakeHearts() {
    // Shake all remaining hearts
    this.hearts.forEach((heart) => {
      if (heart.texture.key === 'heart') {
        this.tweens.add({
          targets: heart,
          x: heart.x + 3,
          duration: 50,
          yoyo: true,
          repeat: 3,
          ease: 'Sine.easeInOut',
        });
      }
    });
    
    // Flash screen red
    this.cameras.main.flash(100, 255, 50, 50);
  }

  protected togglePause() {
    this.isPaused = !this.isPaused;
    
    if (this.isPaused) {
      this.physics.pause();
      this.showPauseMenu();
    } else {
      this.physics.resume();
      this.hidePauseMenu();
    }
  }

  protected pauseMenu?: Phaser.GameObjects.Container;

  protected showPauseMenu() {
    const { width, height } = GAME_CONFIG;
    
    this.pauseMenu = this.add.container(width / 2, height / 2);
    this.pauseMenu.setDepth(300);

    const bg = this.add.rectangle(0, 0, 280, 260, 0x000000, 0.9);
    const title = this.add.text(0, -100, 'PAUSED', {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ffffff',
    }).setOrigin(0.5);
    
    // Controls reference
    const controlsTitle = this.add.text(0, -65, '🎮 Controls', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffd700',
    }).setOrigin(0.5);
    
    const controlsText = this.add.text(0, -25, 
      '↑ Jump | SHIFT Dash\n' +
      'ESC/P Pause',
    {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#aaaaaa',
      align: 'center',
    }).setOrigin(0.5);

    const resumeBtn = this.add.text(0, 30, '▶ Resume', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#88ff88',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    resumeBtn.on('pointerdown', () => this.togglePause());

    const quitBtn = this.add.text(0, 75, '✕ Quit to Map', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#ff8888',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    quitBtn.on('pointerdown', () => {
      this.scene.start('WorldMapScene');
    });

    this.pauseMenu.add([bg, title, controlsTitle, controlsText, resumeBtn, quitBtn]);
  }

  protected hidePauseMenu() {
    this.pauseMenu?.destroy();
    this.pauseMenu = undefined;
  }

  protected onVictory() {
    this.isGameOver = true;
    this.physics.pause();

    const { width, height } = GAME_CONFIG;

    // Calculate stars
    const healthPercent = this.player.getHealth() / PLAYER_CONFIG.maxHealth;
    const stars = healthPercent >= 0.8 ? 3 : healthPercent >= 0.5 ? 2 : 1;

    // Save progress
    GameStateManager.completeLevel(this.config.levelId, { stars });
    
    // Get next level info
    const nextLevel = GameStateManager.getNextLevel(this.config.levelId);
    const nextLevelName = nextLevel ? GameStateManager.getLevelName(nextLevel) : null;

    // Victory screen
    const container = this.add.container(width / 2, height / 2).setDepth(300);

    const bg = this.add.rectangle(0, 0, 320, nextLevel ? 340 : 280, 0x000000, 0.9);
    const title = this.add.text(0, -120, '🎉 COMPLETE!', {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ffd700',
    }).setOrigin(0.5);

    const levelName = this.add.text(0, -85, this.config.levelName, {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#a78bfa',
    }).setOrigin(0.5);

    const starText = this.add.text(0, -45, '⭐'.repeat(stars) + '☆'.repeat(3 - stars), {
      fontSize: '32px',
    }).setOrigin(0.5);

    const distText = this.add.text(0, 0, `Distance: ${Math.floor(this.distance)}m`, {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffffff',
    }).setOrigin(0.5);

    const collectText = this.add.text(0, 25, `Collected: ${this.collected}`, {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffdd44',
    }).setOrigin(0.5);

    container.add([bg, title, levelName, starText, distText, collectText]);

    // Next level unlocked message
    if (nextLevelName) {
      const unlockedText = this.add.text(0, 60, `🔓 UNLOCKED: ${nextLevelName}`, {
        fontFamily: 'Georgia, serif',
        fontSize: '12px',
        color: '#22c55e',
      }).setOrigin(0.5);
      container.add(unlockedText);
    }

    // Map button
    const mapBtn = this.add.text(0, nextLevel ? 100 : 80, '🗺️ World Map', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#ffd700',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    mapBtn.on('pointerover', () => mapBtn.setScale(1.1));
    mapBtn.on('pointerout', () => mapBtn.setScale(1));
    mapBtn.on('pointerdown', () => {
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.time.delayedCall(300, () => {
        this.scene.start('WorldMapScene');
      });
    });
    container.add(mapBtn);

    // Next level button
    if (nextLevel) {
      const nextBtn = this.add.text(0, 135, '▶ Next Level', {
        fontFamily: 'Georgia, serif',
        fontSize: '18px',
        color: '#22c55e',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      
      nextBtn.on('pointerover', () => nextBtn.setScale(1.1));
      nextBtn.on('pointerout', () => nextBtn.setScale(1));
      nextBtn.on('pointerdown', () => {
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.time.delayedCall(300, () => {
          const nextSceneKey = GameStateManager.getLevelSceneKey(nextLevel);
          this.scene.start(nextSceneKey);
        });
      });
      container.add(nextBtn);
    }
  }

  protected onDefeat() {
    this.isGameOver = true;
    this.physics.pause();

    const { width, height } = GAME_CONFIG;

    const container = this.add.container(width / 2, height / 2).setDepth(300);

    const bg = this.add.rectangle(0, 0, 280, 220, 0x000000, 0.9);
    const title = this.add.text(0, -70, '💔 GAME OVER', {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ff6666',
    }).setOrigin(0.5);

    const distText = this.add.text(0, -20, `Distance: ${Math.floor(this.distance)}m`, {
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      color: '#aaaaaa',
    }).setOrigin(0.5);

    const retryBtn = this.add.text(0, 30, '↻ Try Again', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#88ff88',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    retryBtn.on('pointerdown', () => this.scene.restart());

    const quitBtn = this.add.text(0, 70, '← Back to Map', {
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      color: '#aaaaaa',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    quitBtn.on('pointerdown', () => {
      this.scene.start('WorldMapScene');
    });

    container.add([bg, title, distText, retryBtn, quitBtn]);
  }

  // Abstract methods for subclasses
  protected abstract startSpawning(): void;
  protected abstract spawnObstacle(): void;
  protected abstract spawnCollectible(): void;
  protected abstract onCollect(): void;

  update(time: number, delta: number) {
    if (this.isPaused || this.isGameOver) return;

    // Update distance
    this.distance += (this.scrollSpeed * delta) / 1000;
    this.distanceText.setText(`${Math.floor(this.distance)}m`);
    this.updateProgressBar();

    // Check win condition
    if (this.distance >= this.config.targetDistance) {
      this.onVictory();
      return;
    }

    // Scroll background
    this.bgLayers.forEach((layer, i) => {
      layer.tilePositionX += this.scrollSpeed * (0.5 + i * 0.3) * (delta / 1000);
    });
    
    // Scroll edge overlay with background
    if (this.edgeOverlay) {
      this.edgeOverlay.tilePositionX += this.scrollSpeed * 0.5 * (delta / 1000);
    }

    // Move obstacles and collectibles left
    this.obstacles.getChildren().forEach((obj) => {
      const sprite = obj as Phaser.Physics.Arcade.Sprite;
      sprite.x -= this.scrollSpeed * (delta / 1000);
      if (sprite.x < -50) sprite.destroy();
    });

    this.collectibles.getChildren().forEach((obj) => {
      const sprite = obj as Phaser.Physics.Arcade.Sprite;
      sprite.x -= this.scrollSpeed * (delta / 1000);
      if (sprite.x < -50) sprite.destroy();
    });

    // Handle player jump with edge physics boost
    if (this.jumpKey?.isDown || this.cursors?.up.isDown || this.cursors?.space?.isDown) {
      if (this.player.isOnGround()) {
        this.player.jumpWithBoost(this.currentJumpBoost);
        this.currentJumpBoost = 1.0; // Reset after use
      }
    }

    // Handle dash
    if (this.dashKey?.isDown) {
      this.player.dash(1);
    }

    // Update mobile controls
    this.mobileControls?.update();
    
    // Apply edge physics (Sobel-based friction/jumping)
    this.applyEdgePhysics();

    // Custom update logic
    this.updateLevel(time, delta);
  }

  protected abstract updateLevel(time: number, delta: number): void;
  
  /**
   * Clean up when scene is destroyed or restarted.
   * Override in subclasses to add specific cleanup.
   */
  shutdown() {
    // Clear any remaining tweens
    this.tweens.killAll();
    
    // Clear pause menu
    if (this.pauseMenu) {
      this.pauseMenu.destroy();
      this.pauseMenu = undefined;
    }
    
    // Clear mobile controls
    this.mobileControls = undefined;
    
    // Reset state
    this.isGameOver = false;
    this.isPaused = false;
  }
}
